import { Selector } from '@/components'
import { BaseModal } from '@/components/shared/Modal/Modal.component'
import { Alert, Api } from '@/modules'
import { CreateKeyMessageRequest, SearchResponse } from '@shared/models'
import { useState } from 'react'
import { useCompanyContext } from '../../CompanyDetails.context'
import { KeyMessageForm } from './KeyMessageForm.component'

interface CreateKeyMessageModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function CreateKeyMessageModal({ isOpen, onClose, onSuccess }: CreateKeyMessageModalProps) {
  const { company } = useCompanyContext()
  const { sendAlert } = Alert.Hook()

  const companyID = company?.id

  const [premises, setPremises] = useState('')
  const [message, setMessage] = useState('')
  const [exclusionPremises, setExclusionPremises] = useState('')
  const [applyToSocial, setApplyToSocial] = useState(false)
  const [applyToPress, setApplyToPress] = useState(false)

  const { binder: brandBinder, selectedOptions: selectedBrands, reset: resetBrands } = Selector.MultiBinder()

  const { isRunning: isCreating, run: createKeyMessage } = Api.Command<void>(Api.Methods.POST, 'key-messages')

  const [isSearchingBrands, , searchedBrands] = Api.Query<SearchResponse[]>(
    `brands/search?company=${companyID}&size=10000`,
  )

  function onExclusionPremisesChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    setExclusionPremises(e.target.value)
  }

  function onApplyToSocialChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setApplyToSocial(e.target.checked)
  }

  function onApplyToPressChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setApplyToPress(e.target.checked)
  }

  function resetForm(): void {
    setPremises('')
    setMessage('')
    setExclusionPremises('')
    setApplyToSocial(false)
    setApplyToPress(false)
    resetBrands()
  }

  function handleClose(): void {
    resetForm()
    onClose()
  }

  function onSubmit(): void {
    if (!premises.trim()) {
      return sendAlert({
        type: 'error',
        title: 'Campo obrigatório',
        message: 'Premissas são obrigatórias',
      })
    }

    if (!message.trim()) {
      return sendAlert({
        type: 'error',
        title: 'Campo obrigatório',
        message: 'Mensagem é obrigatória',
      })
    }

    if (!applyToSocial && !applyToPress) {
      return sendAlert({
        type: 'error',
        title: 'Campo obrigatório',
        message: 'É necessário selecionar pelo menos uma opção entre Redes Sociais e Imprensa',
      })
    }

    if (selectedBrands.length === 0) {
      return sendAlert({
        type: 'error',
        title: 'Campo obrigatório',
        message: 'É necessário selecionar pelo menos uma marca',
      })
    }

    let dataSource: number | undefined
    if (applyToSocial && !applyToPress) dataSource = 1
    else if (!applyToSocial && applyToPress) dataSource = 2

    const input: CreateKeyMessageRequest = {
      companyID: companyID!,
      premises: premises.trim(),
      message: message.trim(),
      dataSourceID: dataSource,
      brandIDs: selectedBrands.map((b) => b.value as string),
      ...(exclusionPremises.trim() && { exclusionPremisses: exclusionPremises.trim() }),
    }

    createKeyMessage<CreateKeyMessageRequest>({
      input,
      onSuccess: () => {
        sendAlert({
          type: 'success',
          title: 'Sucesso',
          message: 'Mensagem-chave cadastrada com sucesso!',
        })
        resetForm()
        onSuccess()
        onClose()
      },
      onFail: () => {
        sendAlert({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao cadastrar mensagem-chave',
        })
      },
    })
  }

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title="Nova Mensagem-Chave" maxWidth="720px">
      <KeyMessageForm
        premises={premises}
        message={message}
        exclusionPremises={exclusionPremises}
        onExclusionPremisesChange={onExclusionPremisesChange}
        isLoading={isCreating}
        onPremisesChange={setPremises}
        onMessageChange={setMessage}
        onSubmit={onSubmit}
        applyToSocial={applyToSocial}
        applyToPress={applyToPress}
        onApplyToSocialChange={onApplyToSocialChange}
        onApplyToPressChange={onApplyToPressChange}
        brandBinder={brandBinder}
        brandOptions={searchedBrands?.map((brand) => ({ text: brand.text, value: brand.value })) || []}
        isLoadingBrands={isSearchingBrands}
      />
    </BaseModal>
  )
}
